/**
 * @description
 * API client for making requests to the admin backend.
 * Provides typed methods for all API endpoints.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 * @see admin/src/frontend/pages/DeploymentDetails.tsx
 */

import { getAuthToken } from '../../lib/auth';

const API_BASE_URL = '/api';

/**
 * Generic API request handler with error handling
 */
async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  // Get auth token from localStorage (if available)
  const token = getAuthToken();
  
  // Build headers with authentication
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };
  
  // Add Authorization header if token exists
  if (token && token !== 'mock-dev-token-intars@byteable.ai') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Include cookies for session-based auth
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `API request failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * API client with typed methods
 */
export const api = {
  // Deployment endpoints
  getDeploymentDetails: (id: string) =>
    request(`/deployments/${id}`),
  
  rollbackDeployment: (id: string) =>
    request(`/deployments/${id}/rollback`, { method: 'POST' }),
  
  deleteDeployment: (id: string) =>
    request(`/deployments/${id}`, { method: 'DELETE' }),
  
  getDeployments: () =>
    request('/deployments'),
  
  getDeploymentHealth: (id: string) =>
    request(`/deployments/${id}/health`),
  
  getDeploymentHealthHistory: (id: string) =>
    request(`/deployments/${id}/health/history`),
  
  scheduleDeploymentHealthChecks: (id: string, intervalMinutes: number) =>
    request(`/deployments/${id}/health/schedule`, { 
      method: 'POST',
      body: JSON.stringify({ intervalMinutes })
    }),
  
  getPerformanceAnalytics: (timeRange: '7d' | '30d' | '90d' = '30d') =>
    request(`/stripe/utilities/performance-analytics?timeRange=${timeRange}`),
  
  // Client endpoints
  getClients: () =>
    request('/clients'),
  
  getClient: (id: string) =>
    request(`/clients/${id}`),
  
  createClient: (data: {
    subdomain: string;
    name: string;
    status?: string;
    createdBy: string;
  }) =>
    request('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateClient: (id: string, data: {
    subdomain?: string;
    name?: string;
    status?: string;
    allowedDomains?: string;
    domainRestrictionEnabled?: boolean;
  }) =>
    request(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteClient: (id: string) =>
    request(`/clients/${id}`, { method: 'DELETE' }),
  
  // User endpoints
  getUsers: () =>
    request('/users'),
  
  getUser: (id: string) =>
    request(`/users/${id}`),
  
  createUser: (data: {
    email: string;
    name: string;
    provider: string;
    role: string;
    status?: string;
  }) =>
    request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateUser: (id: string, data: {
    email?: string;
    name?: string;
    provider?: string;
    role?: string;
    status?: string;
  }) =>
    request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteUser: (id: string) =>
    request(`/users/${id}`, { method: 'DELETE' }),
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */